import { useState, useEffect } from 'react';

interface UseConsentOptions {
  required: boolean;
  version: string;
  storageKey: string;
  remember: boolean;
}

export function useConsent({ required, version, storageKey, remember }: UseConsentOptions) {
  const [hasConsent, setHasConsent] = useState<boolean>(() => {
    if (!required) return true;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return false;
      const parsed = JSON.parse(raw) as { accepted?: boolean; version?: string };
      if (!parsed?.accepted) return false;
      if (version && parsed.version && parsed.version !== version) return false;
      return true;
    } catch {
      return false;
    }
  });

  // Re-validate when config changes
  useEffect(() => {
    if (!required) {
      setHasConsent(true);
      return;
    }
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        setHasConsent(false);
        return;
      }
      const parsed = JSON.parse(raw) as { accepted?: boolean; version?: string };
      if (!parsed?.accepted) {
        setHasConsent(false);
        return;
      }
      if (version && parsed.version && parsed.version !== version) {
        setHasConsent(false);
        return;
      }
      setHasConsent(true);
    } catch {
      setHasConsent(false);
    }
  }, [required, storageKey, version]);

  // Persist consent decisions
  useEffect(() => {
    if (!required || !remember) return;
    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify({ accepted: hasConsent, version, timestamp: new Date().toISOString() })
      );
    } catch {
      // ignore storage errors
    }
  }, [required, remember, storageKey, version, hasConsent]);

  return { hasConsent, setHasConsent };
}
